/**
 * Frontend to Backend Type Transformers
 *
 * This file contains transformation functions to convert frontend training sprint
 * creation types to backend Sprint types for API calls.
 *
 * Frontend types are optimized for UI/UX while backend types are optimized for
 * data storage and runtime tracking.
 */

import { Sprint, SprintParticipant, SprintTeam, SprintActivity } from '../../../../../../pure-js/types/Sprint'
import { SprintPurpose } from '../../../../../../pure-js/enums/SprintPurpose'
import { sanitizeSprintActivityGameOptions } from '../../../../../../pure-js/libs/SprintActivityGameOptions'
import {
  TrainingSprintConfig,
  Participant as FEParticipant,
  Team as FETeam,
  Activity as FEActivity,
  TrainingPurpose as FETrainingPurpose
} from '../types'
import { getActivityTemplateByType } from '../constants/activities'
import { getDisplayDurationMinutes, getResolvedLoopCount } from '../utils/activityConfiguration'

/**
 * Transform frontend TrainingPurpose to backend SprintPurpose
 *
 * @param {FETrainingPurpose} fePurpose - Frontend sprint purpose.
 * @return {SprintPurpose} Backend sprint purpose.
 */
export function transformPurposeToBE(fePurpose: FETrainingPurpose): SprintPurpose {
  const mapping: Record<FETrainingPurpose, SprintPurpose> = {
    [FETrainingPurpose.AWARE]: SprintPurpose.AWARENESS,
    [FETrainingPurpose.TRAIN]: SprintPurpose.DEVELOPMENT,
    [FETrainingPurpose.CUSTOM]: SprintPurpose.CUSTOM
  }
  return mapping[fePurpose]
}

/**
 * Combine date and time strings into ISO datetime string
 *
 * @param {string | undefined} date - Date string (e.g. "2024-01-15").
 * @param {string | undefined} time - Time string (e.g. "14:30").
 * @return {string} ISO datetime string or current time fallback.
 */
function combineDateTime(date?: string, time?: string): string {
  if (!date || !time) {
    // Return today's date/time as placeholder if schedule not set yet
    // This allows draft sprints to save activities before they're scheduled
    const now = new Date()
    return now.toISOString()
  }

  try {
    // Parse the date and time
    const [year, month, day] = date.split('-').map(Number)
    const [hours, minutes] = time.split(':').map(Number)

    // Create a Date object (months are 0-indexed in JS)
    const dateTime = new Date(year, month - 1, day, hours, minutes)

    // Return ISO string
    return dateTime.toISOString()
  } catch (error) {
    // Return current date/time as fallback
    return new Date().toISOString()
  }
}

/**
 * Calculate end date from start date and duration (in minutes)
 *
 * @param {string} startDate - ISO datetime string.
 * @param {number} durationMinutes - Duration in minutes.
 * @return {string} ISO datetime string.
 */
function calculateEndDate(startDate: string, durationMinutes: number): string {
  if (!startDate || !durationMinutes) {
    // If no start date, use now + duration as placeholder
    const now = new Date()
    const end = new Date(now.getTime() + (durationMinutes || 60) * 60000)
    return end.toISOString()
  }

  try {
    const start = new Date(startDate)
    const end = new Date(start.getTime() + durationMinutes * 60000) // 60000 ms per minute
    return end.toISOString()
  } catch (error) {
    // Return current date + duration as fallback
    const now = new Date()
    const end = new Date(now.getTime() + (durationMinutes || 60) * 60000)
    return end.toISOString()
  }
}

/**
 * Reserved team ID for unassigned participants
 * Format: unassigned_<epochTime>
 */
export const UNASSIGNED_TEAM_ID_PREFIX = 'unassigned_'

/**
 * Generate a reserved team ID for unassigned participants
 *
 * @return {string} Reserved team id.
 */
export function generateUnassignedTeamId(): string {
  const epochTime = Date.now()
  return `${UNASSIGNED_TEAM_ID_PREFIX}${epochTime}`
}

/**
 * Check if a team ID is an unassigned placeholder
 *
 * @param {string | undefined} teamId - Team id to inspect.
 * @return {boolean} True when the id uses the unassigned prefix.
 */
export function isUnassignedTeamId(teamId?: string): boolean {
  return teamId?.startsWith(UNASSIGNED_TEAM_ID_PREFIX) || false
}

/**
 * Transform frontend Participant to backend SprintParticipant
 *
 * Note: userId and teamId will be set by backend or during team transformation
 *
 * @param {FEParticipant} feParticipant - Frontend participant.
 * @param {string | undefined} teamId - Team id for the participant.
 * @return {SprintParticipant} Backend participant.
 */
export function transformParticipantToBE(feParticipant: FEParticipant, teamId?: string): SprintParticipant {
  return {
    name: feParticipant.name,
    email: feParticipant.email || undefined,
    teamId: teamId || undefined // Will be set during team transformation or left undefined
    // userId will be set by backend when participant is matched to existing user
  }
}

/**
 * Transform frontend Team to backend SprintTeam
 *
 * @param {FETeam} feTeam - Frontend team.
 * @return {SprintTeam} Backend team.
 */
export function transformTeamToBE(feTeam: FETeam): SprintTeam {
  return {
    name: feTeam.name,
    tempTeamId: feTeam.id, // FE id becomes tempTeamId for backend tracking
    // Keep the UI explicit, but never let an invalid payload send an empty language to the backend.
    language: feTeam.language || 'en'
    // teamId will be set by backend after team is created in database
  }
}

/**
 * Transform frontend Activity to backend SprintActivity
 *
 * @param {FEActivity} feActivity - Frontend activity.
 * @return {SprintActivity} Backend activity.
 */
export function transformActivityToBE(feActivity: FEActivity): SprintActivity {
  const template = getActivityTemplateByType(feActivity.type)
  const sanitizedRuntimeConfig = sanitizeSprintActivityGameOptions(feActivity)
  const sanitizedTemplateRuntimeConfig = sanitizeSprintActivityGameOptions(template)

  // Combine scheduledDate and scheduledTime into full datetime
  const startDate = combineDateTime(feActivity.scheduledDate, feActivity.scheduledTime)
  const durationMinutes = getDisplayDurationMinutes(feActivity)

  // Calculate end date from start date + duration (use loop-based duration for game activities)
  const endDate =
    startDate && durationMinutes
      ? calculateEndDate(startDate, durationMinutes)
      : combineDateTime(feActivity.scheduledDate, feActivity.endTime)

  return {
    id: feActivity.id,
    title: feActivity.title,
    description: feActivity.description || undefined,
    startDate: startDate,
    endDate: endDate,
    endedAt: '', // Will be set by backend when activity actually ends
    completedActivityTeamIds: [], // Will be tracked by backend during gameplay
    emailsSent: [], // Will be set by backend to track email sent status per user
    type: feActivity.type,
    numberOfLoops:
      getResolvedLoopCount(feActivity.type, sanitizedRuntimeConfig.numberOfLoops) ??
      sanitizedTemplateRuntimeConfig.numberOfLoops,
    customSlotLength: sanitizedRuntimeConfig.customSlotLength ?? sanitizedTemplateRuntimeConfig.customSlotLength,
    minTimeLeftToAllowRestart:
      sanitizedRuntimeConfig.minTimeLeftToAllowRestart ?? sanitizedTemplateRuntimeConfig.minTimeLeftToAllowRestart,
    finalReflectionTimeOut:
      sanitizedRuntimeConfig.finalReflectionTimeOut ?? sanitizedTemplateRuntimeConfig.finalReflectionTimeOut,
    // not shown in the wizard, carried through so saving the sprint keeps the activity's version
    ...(feActivity.dataCustomizationVariantId && { dataCustomizationVariantId: feActivity.dataCustomizationVariantId })
  }
}

/**
 * Extract all participants from teams and assign them their teamId
 *
 * Frontend stores participants inside teams, but backend stores teamId inside participants.
 * This function flattens the structure.
 *
 * @param {FETeam[]} feTeams - Frontend teams.
 * @return {SprintParticipant[]} Flattened backend participants.
 */
export function extractParticipantsFromTeams(feTeams: FETeam[]): SprintParticipant[] {
  const allParticipants: SprintParticipant[] = []

  for (const team of feTeams) {
    for (const participant of team.participants) {
      allParticipants.push(transformParticipantToBE(participant, team.id))
    }
  }

  return allParticipants
}

/**
 * Calculate minParticipantsPerTeam from team configurations
 *
 * @param {FETeam[]} feTeams - Frontend teams.
 * @return {number} Minimum participants per team.
 */
function calculateMinParticipantsPerTeam(feTeams: FETeam[]): number {
  if (feTeams.length === 0) {
    return 2 // Default minimum
  }

  // Find the minimum number of participants across all teams
  const minParticipants = Math.min(...feTeams.map((t) => t.participants.length))

  // Return at least 2 (or the minimum found)
  return Math.max(2, minParticipants)
}

/**
 * Calculate maxParticipantsPerTeam from team configurations
 *
 * @param {FETeam[]} feTeams - Frontend teams.
 * @return {number} Maximum participants per team.
 */
function calculateMaxParticipantsPerTeam(feTeams: FETeam[]): number {
  if (feTeams.length === 0) {
    return 6 // Default maximum
  }

  // Use the maximum of maxParticipants specified in teams
  const maxFromConfig = Math.max(...feTeams.map((t) => t.maxParticipants))

  // Also consider actual participant counts
  const maxActual = Math.max(...feTeams.map((t) => t.participants.length))

  // Return the larger of the two
  return Math.max(maxFromConfig, maxActual, 2) // At least 2
}

/**
 * Main transformation function: Frontend TrainingSprintConfig to Backend Sprint
 *
 * This transforms the complete frontend training sprint configuration into
 * the backend Sprint format ready for API submission.
 *
 * Note: Backend will add:
 * - id (generated)
 * - status (set to INACTIVE initially)
 * - facilitatedByUserIds (current user)
 * - createdAt (timestamp)
 * - reports (empty initially)
 * - teamHistory (empty initially)
 *
 * @param {TrainingSprintConfig} feConfig - Frontend sprint configuration.
 * @return {Partial<Sprint>} Partial backend sprint payload.
 */
export function transformTrainingSprintToBE(feConfig: TrainingSprintConfig): Partial<Sprint> {
  // Extract participants from teams (those with team assignments)
  const assignedParticipants = extractParticipantsFromTeams(feConfig.teams)

  // Get IDs of participants that are already in teams
  const assignedParticipantIds = new Set(assignedParticipants.map((p) => p.email))

  // Get unassigned participants (not in any team)
  const unassignedParticipantsList = feConfig.participants.filter((p) => !assignedParticipantIds.has(p.email))

  // Generate a reserved team ID for unassigned participants
  const unassignedTeamId = unassignedParticipantsList.length > 0 ? generateUnassignedTeamId() : undefined

  // Transform unassigned participants with the reserved team ID
  const unassignedParticipants = unassignedParticipantsList.map((p) => transformParticipantToBE(p, unassignedTeamId))

  // Combine all participants: assigned + unassigned
  const participants = [...assignedParticipants, ...unassignedParticipants]

  // Transform teams
  const teams = feConfig.teams.map(transformTeamToBE)

  // Transform activities (uses placeholder dates for unscheduled activities)
  const activities = feConfig.activities.map(transformActivityToBE)

  // Calculate team size constraints
  const minParticipantsPerTeam = calculateMinParticipantsPerTeam(feConfig.teams)
  const maxParticipantsPerTeam = calculateMaxParticipantsPerTeam(feConfig.teams)

  return {
    // Required fields
    title: feConfig.sprintName || 'Untitled Training Sprint',
    purpose: transformPurposeToBE(feConfig.purpose),
    participants,
    teams,
    activities,
    reportIds: [], // Required field - initialize as empty array

    // Optional fields
    startDate: feConfig.startDate || undefined,
    endDate: feConfig.endDate || undefined,
    minParticipantsPerTeam,
    maxParticipantsPerTeam,
    emailContent: feConfig.emailContent || undefined,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone

    // Backend will add these fields:
    // - id: Generated by backend
    // - status: Set to INACTIVE initially
    // - facilitatedByUserIds: Current user added by backend
    // - createdAt: Timestamp added by backend
    // - teamHistory: Empty array initially
    // - sendOutAt: Can be set later
  }
}

/**
 * Validate that the frontend config has all required data for backend transformation
 *
 * @param {TrainingSprintConfig} feConfig - Frontend sprint configuration.
 * @return {string[]} Array of validation error messages.
 */
export function validateForTransformation(feConfig: TrainingSprintConfig): string[] {
  const errors: string[] = []

  // Check required fields
  if (!feConfig.purpose) {
    errors.push('Purpose is required')
  }

  if (!feConfig.sprintName && feConfig.purpose !== FETrainingPurpose.CUSTOM) {
    // Sprint name might be optional for non-custom sprints (can use default)
    // But warn if missing
  }

  if (!feConfig.teams || feConfig.teams.length === 0) {
    if (!feConfig.participants || feConfig.participants.length === 0) {
      errors.push('At least one participant or team is required')
    }
  }

  if (!feConfig.activities || feConfig.activities.length === 0) {
    errors.push('At least one activity is required')
  }

  // Check that all activities have scheduled dates
  const unscheduledActivities = feConfig.activities.filter((a) => !a.scheduledDate || !a.scheduledTime)
  if (unscheduledActivities.length > 0) {
    errors.push(`${unscheduledActivities.length} activities are missing schedule information`)
  }

  // Check team structure
  for (const team of feConfig.teams) {
    if (team.participants.length === 0) {
      errors.push(`Team "${team.name}" has no participants`)
    }
    if (team.participants.length > team.maxParticipants) {
      errors.push(`Team "${team.name}" has more participants than the maximum allowed`)
    }
  }

  return errors
}
